import { useCallback } from "react";

import { FaRegFileArchive } from "react-icons/fa";
import { FaRegFileImage, FaRegFilePdf } from "react-icons/fa6";
import {
  FcFile,
  FcFolder,
  FcLink,
  FcVideoCall,
} from "react-icons/fc";
import {  RiFileExcel2Line, RiFilePpt2Line } from "react-icons/ri";
import { TbFileTypeZip } from "react-icons/tb";

/**
 * Utility hook for common document operations
 */
export const useDocumentUtils = () => {
  /**
   * Format file size in human readable format
   */
  const formatSize = useCallback((sizeInBytes) => {
    const units = ["bytes", "KB", "MB", "GB", "TB"];
    let size = sizeInBytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(2)} ${units[unitIndex]}`;
  }, []);

  /**
   * Determine if icon should be shown based on hover or selection state
   */
  const showIcon = useCallback(
    (index, hoveredIndex, selectedIndex, selectedForDelete, documents) => {
      return (
        hoveredIndex === index ||
        selectedIndex === index ||
        selectedForDelete.some((item) => item.id === documents[index].id)
      );
    },
    []
  );

  /**
   * Get file type icon component
   */
  const getFileIcon = useCallback((fileType) => {
    // const {
    //   FcFolder,
    //   FaRegFilePdf,
    //   FaRegFileImage,
    //   RiFilePpt2Line,
    //   FcVideoCall,
    //   TbFileTypeZip,
    //   FaRegFileArchive,
    //   RiFileExcel2Line,
    //   FcLink,
    //   FcFile,
    // } = require("react-icons/all");

    const iconStyle = { marginRight: 5, marginBottom: 3, size: 16 };

    if (fileType.includes("folder")) {
      return <FcFolder size={iconStyle.size} style={iconStyle} />;
    } else if (fileType.includes("pdf")) {
      return (
        <FaRegFilePdf size={iconStyle.size} color="red" style={iconStyle} />
      );
    } else if (
      fileType.includes("jpg") ||
      fileType.includes("png") ||
      fileType.includes("jpeg")
    ) {
      return (
        <FaRegFileImage
          size={iconStyle.size}
          color="#35A29F"
          style={iconStyle}
        />
      );
    } else if (fileType.includes("pptx") || fileType.includes("presentation")) {
      return (
        <RiFilePpt2Line
          size={iconStyle.size}
          color="#B94623"
          style={iconStyle}
        />
      );
    } else if (fileType.includes("mp4")) {
      return (
        <FcVideoCall size={iconStyle.size} color="#5C6E91" style={iconStyle} />
      );
    } else if (fileType.includes("x-zip-compressed")) {
      return (
        <TbFileTypeZip
          size={iconStyle.size}
          color="#5A509B"
          style={iconStyle}
        />
      );
    } else if (fileType.includes("x-compressed")) {
      return (
        <FaRegFileArchive
          size={iconStyle.size}
          color="#5A509B"
          style={iconStyle}
        />
      );
    } else if (fileType.includes("spreadsheet")) {
      return (
        <RiFileExcel2Line
          size={iconStyle.size}
          color="#009440"
          style={iconStyle}
        />
      );
    } else if (fileType.includes("link")) {
      return <FcLink size={iconStyle.size} style={iconStyle} />;
    } else {
      return <FcFile size={iconStyle.size} style={iconStyle} />;
    }
  }, []);

  /**
   * Get breadcrumb path from folder path string
   */
  const getUnitName = useCallback((unitName) => {
    const { applyClassToCharacters } = require("../../../constants/const");
    const { GetSelectedLanguage } = require("../../helper/Method");
    const li = (item, index) => {
      const { React } = require("react");
      return (
        <li
          className={
            GetSelectedLanguage() === "ur" ? "breadcrumb-item-ur" : "breadcrumb-item"
          }
          key={index}
        >
          <span>{applyClassToCharacters(item, "ur")}</span>
        </li>
      );
    };

    return (
      <ol className="breadcrumb" style={{ backgroundColor: "transparent" }}>
        {unitName?.split("/").map((item, index) => li(item, index))}
      </ol>
    );
  }, []);

  return {
    formatSize,
    showIcon,
    getFileIcon,
    getUnitName,
  };
};

/**
 * Style constants for the component
 */
export const DOCUMENT_STYLES = {
  overlayStyle: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },

  buttonStyle: {
    width: "180px",
    height: "100px",
    margin: "0 10px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: "grey",
    fontSize: "1.2rem",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    border: "none",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.2)",
    borderRadius: "15px",
    transform: "translateY(0)",
  },

  dragOverlayStyle: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "2px dashed #cccccc",
    borderRadius: "4px",
  },

  dragOverlayErrorStyle: {
    backgroundColor: "rgba(250, 197, 201, 0.8)",
    cursor: "not-allowed",
  },

  dragOverlayDefaultStyle: {
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    cursor: "default",
  },

  selectionBoxStyle: {
    position: "fixed",
    fontSize: "24px",
    color: "#333",
    border: "1px solid #9ce5cd",
    backgroundColor: "rgba(156, 229, 205, 0.2)",
    pointerEvents: "none",
  },

  customSelectStyles: {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "20px",
      minHeight: "30px",
      border: state.isFocused ? "1px solid #1AB394" : "",
      boxShadow: state.isFocused ? "0 0 0 1px #1AB394" : "none",
      "&:hover": {
        border: "1px solid #1AB394",
      },
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "15px",
      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.3)",
    }),
    option: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      backgroundColor: state.isSelected ? "#1AB394" : "white",
      color: state.isSelected ? "white" : "black",
      "&:hover": {
        backgroundColor: "#BAE8DE",
        color: "black",
      },
    }),
  },
};
